// LE-00 (promo): リッチメニュータップ（promo_events.eventType='richmenu_tap'）を
// ボタン項目（payload.item）別に集計する（pure / 非破壊）。
//
// 興味タグ分布（customerStats.ts）とは別軸の分析。興味タグは「業態への関心の確度」を
// users に denormalize して集計するが、こちらは「どのリッチメニュー項目が何回タップされたか」
// を行動ログ（promo_events）から直接集計する。item は users に denormalize されていないため、
// 専用クエリ（fetchPromoEventsByEventType）で richmenu_tap を取得して本関数に渡す。
//
// ラベル解決（RICHMENU_ITEM_LABELS）は表示層（RichmenuTapAnalytics）に委ね、本関数は
// raw な item キー（欠落は null）のまま返す（pure / Web SDK 非依存）。

import type { Iso8601, PromoEvent } from '@kawai/shared';
import type { RichmenuTabLayout } from './richmenuLayout.js';

/**
 * 分析で取得する richmenu_tap の上限件数。
 *
 * Firestore クエリの `limit`。これを超えるタップがある場合、集計は**直近この件数分**に
 * なる（`RichmenuTapSummary.capped=true` で UI が「直近 N 件」を明示する）。現状 prod は
 * 反応者十数人規模で十分。将来この上限に張り付くようなら、上限引き上げ or サーバ側の
 * 集計（denormalize / 集計バッチ）への移行を検討する。
 */
export const RICHMENU_TAP_FETCH_LIMIT = 2000;

/** 1 リッチメニュー項目分の集計。 */
export interface RichmenuTapStat {
  /** payload.item。欠落（item 無しボタン / 旧データ）は null。 */
  item: string | null;
  /** 延べタップ回数。 */
  taps: number;
  /** タップしたユニークユーザー数（userId 重複排除）。 */
  users: number;
}

/** リッチメニュータップ分析のサマリー。 */
export interface RichmenuTapSummary {
  /** taps 降順（同数は item 昇順 / null は末尾）に並んだ項目別集計。 */
  rows: RichmenuTapStat[];
  /** 全項目の延べタップ回数。 */
  totalTaps: number;
  /** リッチメニューをタップしたユニークユーザー数（全項目横断）。 */
  uniqueUsers: number;
  /**
   * 取得上限（`fetchLimit`）に到達したか。true なら集計は直近 `fetchLimit` 件分で、
   * 総数を過小集計している可能性がある（UI で「直近 N 件」を明示する）。
   */
  capped: boolean;
}

// 同数タイブレーク用の sentinel。null（項目不明）を昇順ソートの末尾に寄せる。
const NULL_SORT_KEY = '￿';

/**
 * richmenu_tap イベント配列を item 別に集計する（pure）。
 *
 * - `eventType !== 'richmenu_tap'` は防御的にスキップ（呼出側でクエリ済みの想定）。
 * - `payload.item` が無いイベントは `item: null`（「その他（項目不明）」として表示）に束ねる。
 * - users は userId（'line:<lineUserId>'）の重複排除カウント。userId 欠落は人数に数えない。
 * - `fetchLimit` を渡すと、入力件数がそれ以上のとき `capped=true`（取得上限に到達 =
 *   総数を過小集計している可能性）。未指定なら `capped=false`（上限なしとみなす）。
 */
export function summarizeRichmenuTaps(
  events: ReadonlyArray<PromoEvent>,
  fetchLimit?: number,
): RichmenuTapSummary {
  const tapsByItem = new Map<string | null, number>();
  const usersByItem = new Map<string | null, Set<string>>();
  const allUsers = new Set<string>();
  let totalTaps = 0;

  for (const ev of events) {
    if (ev.eventType !== 'richmenu_tap') continue;
    const item = ev.payload?.item ?? null;
    totalTaps += 1;
    tapsByItem.set(item, (tapsByItem.get(item) ?? 0) + 1);
    let set = usersByItem.get(item);
    if (!set) {
      set = new Set<string>();
      usersByItem.set(item, set);
    }
    if (ev.userId) {
      set.add(ev.userId);
      allUsers.add(ev.userId);
    }
  }

  const rows: RichmenuTapStat[] = [...tapsByItem.entries()]
    .map(([item, taps]) => ({
      item,
      taps,
      users: usersByItem.get(item)?.size ?? 0,
    }))
    .sort((a, b) => {
      if (b.taps !== a.taps) return b.taps - a.taps;
      const ai = a.item ?? NULL_SORT_KEY;
      const bi = b.item ?? NULL_SORT_KEY;
      return ai < bi ? -1 : ai > bi ? 1 : 0;
    });

  const capped = fetchLimit !== undefined && events.length >= fetchLimit;

  return { rows, totalTaps, uniqueUsers: allUsers.size, capped };
}

// ───────────────────────────────────────────────────────────
// 顧客一覧フィルター用: userId → タップした item キー集合（pure）
// ───────────────────────────────────────────────────────────

/**
 * richmenu_tap イベント配列から `userId → タップした item キー集合` のマップを作る（pure）。
 *
 * 顧客一覧の「各リッチメニューボタン」フィルター（item 別 OR 絞り込み）用。AppUser には item 別の
 * タップ実績が denormalize されていない（`eventTypeCounts.richmenu_tap` は回数のみで item を区別しない）
 * ため、行動ログ（promo_events）から直接 userId 単位で item 集合を組み立てる。`userId` は
 * `AppUser.uid` と一致する（`fetchPromoEventsByUserId` が uid で引いていることと整合）。
 *
 * - `eventType !== 'richmenu_tap'` はスキップ（呼出側でクエリ済みの想定）。
 * - `payload.item` 欠落 / `userId` 欠落のイベントはマップに寄与しない（フィルター対象にできないため）。
 */
export function buildUserItemTapMap(
  events: ReadonlyArray<PromoEvent>,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const ev of events) {
    if (ev.eventType !== 'richmenu_tap') continue;
    const item = ev.payload?.item;
    if (!item || !ev.userId) continue;
    let set = map.get(ev.userId);
    if (!set) {
      set = new Set<string>();
      map.set(ev.userId, set);
    }
    set.add(item);
  }
  return map;
}

// ───────────────────────────────────────────────────────────
// 顧客詳細ページ用: 1 ユーザーの richmenu_tap を item 別に集計（pure）
// ───────────────────────────────────────────────────────────

/** 1 ユーザー × 1 リッチメニュー項目分の集計（タップ回数 + 最終タップ日時）。 */
export interface UserRichmenuTapStat {
  /** payload.item。欠落（item 無しボタン / 旧データ）は null。 */
  item: string | null;
  /** そのユーザーの当該項目の延べタップ回数。 */
  taps: number;
  /** 最終タップ日時（当該項目の richmenu_tap で最も新しい createdAt / JST ISO8601）。 */
  lastTapAt: Iso8601;
}

/** 1 ユーザーのリッチメニュータップ集計サマリー。 */
export interface UserRichmenuTapSummary {
  /** taps 降順（同数は最終タップ日時の新しい順 / さらに同なら item 昇順・null 末尾）。 */
  rows: UserRichmenuTapStat[];
  /** 全項目の延べタップ回数。 */
  totalTaps: number;
}

/**
 * 1 ユーザーの行動ログ（`fetchPromoEventsByUserId` の結果）から richmenu_tap を item 別に
 * 集計する（pure）。リッチメニュータップ分析（集計版）と同じカード配置を顧客詳細でも使うため、
 * 「タップ回数」と「最終タップ日時」を項目別に返す。
 *
 * - `eventType !== 'richmenu_tap'` はスキップ（ユーザーの全行動ログから richmenu_tap だけ抽出）。
 * - `payload.item` 欠落は `item: null`（「その他（項目不明）」として表示）に束ねる。
 * - createdAt は JST 同一オフセットの ISO8601 のため、文字列の辞書順最大 = 最新。
 */
export function summarizeUserRichmenuTaps(
  events: ReadonlyArray<PromoEvent>,
): UserRichmenuTapSummary {
  const tapsByItem = new Map<string | null, number>();
  const lastByItem = new Map<string | null, Iso8601>();
  let totalTaps = 0;

  for (const ev of events) {
    if (ev.eventType !== 'richmenu_tap') continue;
    const item = ev.payload?.item ?? null;
    totalTaps += 1;
    tapsByItem.set(item, (tapsByItem.get(item) ?? 0) + 1);
    const prev = lastByItem.get(item);
    if (prev === undefined || ev.createdAt > prev) {
      lastByItem.set(item, ev.createdAt);
    }
  }

  const rows: UserRichmenuTapStat[] = [...tapsByItem.entries()]
    .map(([item, taps]) => ({
      item,
      taps,
      lastTapAt: lastByItem.get(item) as Iso8601,
    }))
    .sort((a, b) => {
      if (b.taps !== a.taps) return b.taps - a.taps;
      if (a.lastTapAt !== b.lastTapAt) return a.lastTapAt < b.lastTapAt ? 1 : -1;
      const ai = a.item ?? NULL_SORT_KEY;
      const bi = b.item ?? NULL_SORT_KEY;
      return ai < bi ? -1 : ai > bi ? 1 : 0;
    });

  return { rows, totalTaps };
}

// ───────────────────────────────────────────────────────────
// M-162: 配置グリッドのヒートマップ / タブ集計 / 順位（pure）
// ───────────────────────────────────────────────────────────

/** タイル面の濃さ（0 = 0 回の既定灰面 / 1〜4 = タブ内最多に対する割合の 4 段）。 */
export type HeatLevel = 0 | 1 | 2 | 3 | 4;

/**
 * タブ内の最多回数に対する割合から面の濃さを決める（pure）。
 *
 * - 0 回 or `maxInTab <= 0` → 0（既定の灰面）
 * - 比 ≤ .25 → 1 / ≤ .5 → 2 / ≤ .75 → 3 / それ以上（最多を含む）→ 4
 */
export function heatLevel(taps: number, maxInTab: number): HeatLevel {
  if (taps <= 0 || maxInTab <= 0) return 0;
  const ratio = taps / maxInTab;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

/** 1 タブ分の集計（見出しチップと棒の分母用）。 */
export interface RichmenuTabStats {
  tab: 'A' | 'B';
  /** タブに配置された item の延べタップ回数。 */
  taps: number;
  /** 全体（summary.totalTaps）に対する % 整数（丸め / 分母 0 → 0）。 */
  share: number;
  /** タブ内の item 別タップ回数の最大（棒の 100%）。 */
  maxTaps: number;
  /** タブ内の item 別ユニーク人数の最大（棒の 100%）。 */
  maxUsers: number;
}

/**
 * 配置（layout）に沿ってタブごとに summary.rows を集計する（pure）。
 * 配置外 item（「その他」）はどのタブにも寄与しない。
 */
export function summarizeTabStats(
  summary: RichmenuTapSummary,
  layout: readonly RichmenuTabLayout[],
): RichmenuTabStats[] {
  const byItem = new Map<string, RichmenuTapStat>();
  for (const row of summary.rows) {
    if (row.item !== null) byItem.set(row.item, row);
  }
  return layout.map((t) => {
    let taps = 0;
    let maxTaps = 0;
    let maxUsers = 0;
    for (const cell of t.rows.flat()) {
      const row = byItem.get(cell.item);
      if (!row) continue;
      taps += row.taps;
      if (row.taps > maxTaps) maxTaps = row.taps;
      if (row.users > maxUsers) maxUsers = row.users;
    }
    const share =
      summary.totalTaps > 0 ? Math.round((taps / summary.totalTaps) * 100) : 0;
    return { tab: t.tab, taps, share, maxTaps, maxUsers };
  });
}

/**
 * タブごとに「そのタブのいずれかのボタンをタップしたユニーク人数」を数える（pure）。
 * summary.rows の item 別 users の和はユニークにならないため raw events から数える。
 * `eventType !== 'richmenu_tap'` / `payload.item` 欠落 / `userId` 欠落は数えない。
 */
export function summarizeTabUniqueUsers(
  events: ReadonlyArray<PromoEvent>,
  layout: readonly RichmenuTabLayout[],
): Record<'A' | 'B', number> {
  const tabByItem = new Map<string, 'A' | 'B'>();
  for (const t of layout) {
    for (const cell of t.rows.flat()) tabByItem.set(cell.item, t.tab);
  }
  const users: Record<'A' | 'B', Set<string>> = { A: new Set(), B: new Set() };
  for (const ev of events) {
    if (ev.eventType !== 'richmenu_tap') continue;
    const item = ev.payload?.item;
    if (!item || !ev.userId) continue;
    const tab = tabByItem.get(item);
    if (!tab) continue;
    users[tab].add(ev.userId);
  }
  return { A: users.A.size, B: users.B.size };
}

/** 順位の棒 1 行分。 */
export interface RichmenuRankRow {
  /** payload.item（配置外・旧データは null）。 */
  item: string | null;
  /** 配置上のタブ（配置外 item / null item は null）。 */
  tab: 'A' | 'B' | null;
  taps: number;
  users: number;
  /** 1 人あたり回数（小数 1 桁に丸め / users 0 は null）。 */
  perUser: number | null;
}

/**
 * 順位の棒用の行を作る（pure）。順序は summary.rows（taps 降順・同数 item 昇順・null 末尾）のまま。
 */
export function rankRows(
  summary: RichmenuTapSummary,
  layout: readonly RichmenuTabLayout[],
): RichmenuRankRow[] {
  const tabByItem = new Map<string, 'A' | 'B'>();
  for (const t of layout) {
    for (const cell of t.rows.flat()) tabByItem.set(cell.item, t.tab);
  }
  return summary.rows.map((r) => ({
    item: r.item,
    tab: r.item !== null ? (tabByItem.get(r.item) ?? null) : null,
    taps: r.taps,
    users: r.users,
    perUser: r.users > 0 ? Math.round((r.taps / r.users) * 10) / 10 : null,
  }));
}
